// © Microsoft Corporation. All rights reserved.
import { Severity } from "./wwAppErrorTracker";
/**
 * This file contains the list of web worker app errors.
 * Refer: libs\diagnostics\src\AppError\AppErrorsCatalog.md for Catalog
 * After adding new app error in this file, also add to libs\diagnostics\src\AppError\AppErrors.ts
 * and make sure that Ids are not repeated.
 */
/**
 * Set default severity
 */
const severity = Severity.Alert;
/**
 * Set default property bag
 */
const pb = {
    build: "",
    customMessage: ""
};
/** App Anon cookies fetch call failed */
export const FetchAppAnonFailed = {
    id: 29005,
    source: "AppAnonCookie.ts",
    severity,
    pb
};
const cacheBustingFileName = "ConfigCacheBustHelper.ts";
const wwResolverFile = "WebWorkerResolver.ts";
/** Blank cache busting url */
export const BlankCacheBustingUrl = {
    id: 29011,
    source: cacheBustingFileName,
    severity,
    pb
};
/** CBID fetch response is invalid */
export const InvalidCbidResponse = {
    id: 29012,
    source: cacheBustingFileName,
    severity,
    pb
};
/** Uncaught exception during CBID */
export const CbidUncaughtErrors = {
    id: 29013,
    source: cacheBustingFileName,
    severity,
    pb
};
/** CBID change is detected */
export const RefreshConfigsCbidError = {
    id: 29014,
    source: wwResolverFile,
    severity,
    pb
};
/** New configs are not being populated after CBID wiped the configs */
export const RefreshConfigsNewConfigsFailed = {
    id: 29015,
    source: wwResolverFile,
    severity,
    pb
};
/** Error parsing config tree */
export const ErrorParsingConfigTree = {
    id: 29020,
    source: wwResolverFile,
    severity,
    pb
};
/** Given configIndexHref is not valid */
export const HrefNotValid = {
    id: 29021,
    source: wwResolverFile,
    severity,
    pb
};
/** Error while parsing config tree */
export const GetConfigTreeError = {
    id: 29022,
    source: wwResolverFile,
    severity,
    pb
};
/** config tree is empty */
export const GetConfigTreeEmpty = {
    id: 29023,
    source: wwResolverFile,
    severity,
    pb
};
/** config is null */
export const GetConfigTreeNull = {
    id: 29024,
    source: wwResolverFile,
    severity,
    pb
};
/** Page scope is not valid */
export const PageScopeInvalid = {
    id: 29025,
    source: wwResolverFile,
    severity,
    pb
};
/** Error while fetching config from CRS */
export const CrsFetchError = {
    id: 29026,
    source: wwResolverFile,
    severity,
    pb
};
/** Error while refreshing config tree */
export const RefreshConfigsError = {
    id: 29027,
    source: wwResolverFile,
    severity,
    pb
};
/** CRS sent non-200 response */
export const ConfigCrsFetchNon200Response = {
    id: 29028,
    source: wwResolverFile,
    severity,
    pb
};
/** Cdn config fetch received non-200 response */
export const ConfigCdnFetchNon200Response = {
    id: 29029,
    source: wwResolverFile,
    severity,
    pb
};
/** configs indexedDB failed to init */
export const IdbConfigInitFailed = {
    id: 29030,
    source: wwResolverFile,
    severity,
    pb
};
/** configs indexedDB failed to read */
export const IdbConfigReadFailed = {
    id: 29031,
    source: wwResolverFile,
    severity,
    pb
};
/** configs indexedDB failed to write */
export const IdbConfigWriteFailed = {
    id: 29032,
    source: wwResolverFile,
    severity,
    pb
};
/** configs indexedDB failed to clear */
export const IdbConfigClearFailed = {
    id: 29033,
    source: wwResolverFile,
    severity,
    pb
};
/** Error Idb executing transaction */
export const IdbTransactionError = {
    id: 29034,
    source: "IndexedDb.ts",
    severity,
    pb
};
const wwIndexedDBCacheFile = "IndexedDBCache.ts";
/** Failed to initialize IndexedDB */
export const FailedToInitIdb = {
    id: 29035,
    source: wwIndexedDBCacheFile,
    severity,
    pb
};
/** Failed to save data in IndexedDB */
export const FailedToSaveIdb = {
    id: 29036,
    source: wwIndexedDBCacheFile,
    severity,
    pb
};
/** Failed to clear data in IndexedDB */
export const FailedToClearIdb = {
    id: 29037,
    source: wwIndexedDBCacheFile,
    severity,
    pb
};
/** Failed to hydrate from IndexedDB */
export const FailedToHydrateIdb = {
    id: 29038,
    source: wwIndexedDBCacheFile,
    severity,
    pb
};
/**
 * Configs served from CDN after CRS fetch failed
 */
export const ConfigsDownloadedByCdn = {
    id: 29039,
    source: wwResolverFile,
    severity: Severity.NoAlert,
    pb
};
const wwLocalNewsFile = "LocalNews.ts";
/** Local news fetch failed */
export const LocalNewsFetchFailed = {
    id: 29040,
    source: wwLocalNewsFile,
    severity,
    pb
};
/** Local news fetch failed for location id */
export const LocalNewsLocationIdFetchFailed = {
    id: 29041,
    source: wwLocalNewsFile,
    severity,
    pb
};
/** Pdp card data fetch failed */
export const FetchPdpDataFailed = {
    id: 29042,
    source: "PdpCardData.ts",
    severity,
    pb
};
/** Content list fetch failed */
export const ContentListFetchFailed = {
    id: 29050,
    source: "ContentList.ts",
    severity,
    pb
};
const wwContentPreviewFile = "ContentPreview.ts";
/** Content preview fetch failed */
export const ContentPreviewFetchFailed = {
    id: 29051,
    source: wwContentPreviewFile,
    severity,
    pb
};
/** Unknown Infopane complex slide type */
export const UnknownComplexInfopaneSlide = {
    id: 29052,
    source: wwContentPreviewFile,
    severity,
    pb
};
/** fetch image failed */
export const ImageFetchFailed = {
    id: 29053,
    source: "Image.ts",
    severity,
    pb
};
const wwOneServiceFeedFile = "OneServiceFeed.ts";
/** River config is null */
export const RiverConfigIsNull = {
    id: 29054,
    source: wwOneServiceFeedFile,
    severity,
    pb
};
/** OneService MyFeed fetch failed */
export const FetchOneServiceMyFeedFailed = {
    id: 29055,
    source: wwOneServiceFeedFile,
    severity,
    pb
};
/** Windows feed fetch failure */
export const WindowsFeedFetchFailed = {
    id: 29056,
    source: "WindowsFeed.ts",
    severity,
    pb
};
export const DesktopFeedEdgeNextConfigIsNull = {
    id: 29057,
    source: "WpoFeed.ts",
    severity,
    pb
};
export const OneServiceFeedResponseEmpty = {
    id: 29058,
    source: wwOneServiceFeedFile,
    severity,
    pb
};
/** Error fetching views content */
export const FetchViewsContentFailed = {
    id: 29060,
    source: "ViewsData.ts",
    severity,
    pb
};
/** Industry News Article fetch call failed */
export const FetchIndustryNewsArticlesFailed = {
    id: 29070,
    source: "IndustryNewsArticles.ts",
    severity,
    pb
};
/** Error processing My Briefing response */
export const FetchMyBriefingFailed = {
    id: 29090,
    source: "MyBriefing.ts",
    severity,
    pb
};
/** Wpo MyFeed fetch failed */
export const WpoFeedFetchFailed = {
    id: 29091,
    source: "WpoFeed.ts",
    severity,
    pb
};
/** fetch wpo feed image failed */
export const WpoFeedImageFetchFailed = {
    id: 29092,
    source: "WpoFeed.ts",
    severity,
    pb
};
/** can not get MUID */
export const WpoFeedGetMuidFailure = {
    id: 29093,
    source: "WpoFeed.ts",
    severity: Severity.NoAlert,
    pb
};
//# sourceMappingURL=wwAppErrors.js.map